import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  HardHat,
  Mail,
  Phone,
  ShieldCheck,
  Smartphone,
} from 'lucide-react';
import { Badge, Button, Callout, cx, Field, Input, PhoneInput, Select, Textarea, useToast } from '../components/ui.jsx';
import LumperApplyForm from '../components/LumperApplyForm.jsx';
import { api } from '../lib/api.js';
import { useAuth } from '../context/AuthContext.jsx';
import BrandMark from '../components/BrandMark.jsx';

const PHONE = '1-855-232-7996';
const PHONE_HREF = 'tel:+18552327996';
const SUPPORT = 'support@3lumpers.com';

const BUSINESS_TYPES = [
  { value: 'receiver', label: 'Warehouse or distribution centre' },
  { value: 'shipper', label: 'Shipper / manufacturer' },
  { value: 'broker', label: 'Freight broker' },
  { value: 'carrier', label: 'Trucking company' },
  { value: '3pl', label: 'Third-party logistics' },
];

const VOLUMES = [
  { value: 'a-few-loads-a-month', label: 'A few trailers a month' },
  { value: '5-20-loads-a-month', label: '5 to 20 trailers a month' },
  { value: '20-80-loads-a-month', label: '20 to 80 trailers a month' },
  { value: '80-plus-loads-a-month', label: 'More than 80 a month' },
];

export default function Signup() {
  const [params] = useSearchParams();
  const [choice, setChoice] = useState(params.get('path') === 'business' ? 'business' : params.get('path') === 'lumper' ? 'lumper' : null);
  const formsRef = useRef(null);

  const choose = (next) => {
    setChoice(next);
    requestAnimationFrame(() => {
      formsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  useEffect(() => {
    document.title =
      choice === 'lumper'
        ? 'Sign up to work as a lumper — 3Lumpers'
        : choice === 'business'
          ? 'Sign your business up — 3Lumpers'
          : 'Sign up — 3Lumpers';
  }, [choice]);

  return (
    <div className="landing">
      <div className="util-bar">
        <div className="util-bar-inner">
          <Link to="/" className="row gap-10">
            <BrandMark size={26} />
            <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 650 }}>3Lumpers</span>
          </Link>
          <div className="grow" />
          <a href={PHONE_HREF} className="row gap-6">
            <Phone size={13} /> {PHONE}
          </a>
          <Link to="/login">Log in</Link>
        </div>
      </div>

      {/* The blunt bit: one photo, two questions, two buttons. */}
      <section className="hero-photo short">
        <div className="hero-copy">
          <h1 className="hero-title">Which one are you?</h1>
          <div className="hero-rule" />
          <div className="doors">
            <button type="button" className={cx('door', choice === 'lumper' && 'picked')} onClick={() => choose('lumper')}>
              <Badge tone="hazard" size="sm" style={{ alignSelf: 'flex-start' }}>
                <HardHat size={12} /> Workers
              </Badge>
              <h2>Are you a lumper?</h2>
              <p>Get unloading work on your phone and paid every Friday.</p>
              <div className="door-foot">
                <span className="btn btn-primary btn-lg btn-block">
                  Sign up now <ArrowRight size={15} />
                </span>
              </div>
            </button>

            <button type="button" className={cx('door', choice === 'business' && 'picked')} onClick={() => choose('business')}>
              <Badge tone="amber" size="sm" style={{ alignSelf: 'flex-start' }}>
                <Building2 size={12} /> Businesses
              </Badge>
              <h2>Are you a business in need of lumper labor?</h2>
              <p>Post the job and we send a crew to your dock.</p>
              <div className="door-foot">
                <span className="btn btn-primary btn-lg btn-block">
                  Sign up now <ArrowRight size={15} />
                </span>
              </div>
            </button>
          </div>
        </div>
      </section>

      <section className="signup-forms" ref={formsRef}>
        <div className="signup-shell">
          {!choice ? (
            <Callout tone="amber" title="Pick one of the two above to get started">
              Lumpers sign up for work. Businesses sign up to hire crews. Two different forms — pick yours
              and it only takes a minute.
            </Callout>
          ) : null}

          {choice === 'lumper' ? (
            <div className="split">
              <div>
                <h2>Sign up to work as a lumper</h2>
                <p className="muted mt-8" style={{ maxWidth: '58ch' }}>
                  Three short steps. We check your details and you start getting offers on your phone.
                </p>
                <div className="card mt-20">
                  <div className="card-body">
                    <LumperApplyForm
                      compact
                      onDone={() => {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="col gap-16">
                <SidePanel
                  title="What you need"
                  items={[
                    'A phone that can take a text message',
                    'Boots and a hi-vis vest (most jobs ask for them)',
                    'A way to get to the dock',
                    'Your tax form and a photo ID — you can send those after',
                  ]}
                />
                <SidePanel
                  title="What you get"
                  items={[
                    'The pay, the pallets and the distance before you say yes',
                    'Nobody chasing you — say no and it never counts against you',
                    'Money in the bank every Friday',
                    'Warehouses rate you, and good work gets you first pick',
                  ]}
                  tone="green"
                />
                <Callout tone="blue" icon={Smartphone}>
                  The app is on iPhone and Android. Sign up here now and we will send you the download link.
                </Callout>
              </div>
            </div>
          ) : null}

          {choice === 'business' ? <BusinessSignup /> : null}
        </div>
      </section>

      <footer className="landing-footer">
        <div className="row gap-10">
          <BrandMark size={28} />
          <span>© {new Date().getFullYear()} 3Lumpers Services LLC</span>
        </div>
        <div className="row gap-20 wrap">
          <a href={PHONE_HREF}>{PHONE}</a>
          <Link to="/">Back to the site</Link>
          <Link to="/login">Log in</Link>
        </div>
      </footer>
    </div>
  );
}

function SidePanel({ title, items, tone = 'amber' }) {
  return (
    <div className="card">
      <div className="card-body">
        <span className="label" data-tone={tone} style={{ color: `var(--${tone === 'green' ? 'green' : 'amber'})` }}>
          {title}
        </span>
        <div className="col gap-10 mt-12">
          {items.map((item) => (
            <span key={item} className="row-top gap-10 muted" style={{ fontSize: 'var(--fs-md)' }}>
              <CheckCircle2 size={15} style={{ color: 'var(--green)', marginTop: 3, flex: '0 0 auto' }} />
              {item}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

const EMPTY_BUSINESS = {
  companyName: '',
  kind: 'receiver',
  contactName: '',
  email: '',
  phone: '',
  password: '',
  address: '',
  city: '',
  state: 'FL',
  zip: '',
  volume: '5-20-loads-a-month',
  notes: '',
};

function BusinessSignup() {
  const toast = useToast();
  const navigate = useNavigate();
  const { reload } = useAuth();
  const [form, setForm] = useState(EMPTY_BUSINESS);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/public/business-signup', form);
      await reload();
      toast.ok('You are signed up', 'Ask for a crew whenever you need one.');
      navigate('/portal');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="split">
      <div>
        <h2>Sign your business up</h2>
        <p className="muted mt-8" style={{ maxWidth: '58ch' }}>
          About a minute. Then you can ask for a crew whenever a trailer turns up and see every job and
          invoice in one place.
        </p>

        <form className="card mt-20" onSubmit={submit}>
          <div className="card-body">
            {error ? <Callout tone="red" className="mb-16">{error}</Callout> : null}
            <div className="apply-form">
              <Field label="Company name" required className="span-2">
                <Input value={form.companyName} onChange={(e) => set('companyName', e.target.value)} />
              </Field>
              <Field label="What kind of business?">
                <Select value={form.kind} onChange={(e) => set('kind', e.target.value)} options={BUSINESS_TYPES} />
              </Field>
              <Field label="How much freight?">
                <Select value={form.volume} onChange={(e) => set('volume', e.target.value)} options={VOLUMES} />
              </Field>
              <Field label="Your name" required>
                <Input value={form.contactName} onChange={(e) => set('contactName', e.target.value)} />
              </Field>
              <Field label="Your phone" required>
                <PhoneInput value={form.phone} onChange={(value) => set('phone', value)} placeholder="(305) 123-4567" />
              </Field>
              <Field label="Work email" required hint="This becomes your login">
                <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
              </Field>
              <Field label="Make a password" required>
                <Input type="password" value={form.password} onChange={(e) => set('password', e.target.value)} />
              </Field>
              <Field label="Dock address" className="span-2">
                <Input value={form.address} onChange={(e) => set('address', e.target.value)} placeholder="1420 Industrial Way" />
              </Field>
              <Field label="City">
                <Input value={form.city} onChange={(e) => set('city', e.target.value)} />
              </Field>
              <div className="row gap-10">
                <Field label="State">
                  <Input value={form.state} onChange={(e) => set('state', e.target.value)} maxLength={2} style={{ maxWidth: 80 }} />
                </Field>
                <Field label="ZIP">
                  <Input value={form.zip} onChange={(e) => set('zip', e.target.value)} />
                </Field>
              </div>
              <Field label="Anything we should know? (optional)" className="span-2">
                <Textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) => set('notes', e.target.value)}
                  placeholder="Floor loaded trailers, mostly early mornings, need a crew that can start at 5am."
                />
              </Field>
            </div>
            <div className="apply-actions">
              <div className="grow dim" style={{ fontSize: 'var(--fs-sm)' }}>
                No contracts and no set-up fee.
              </div>
              <Button type="submit" variant="primary" size="lg" loading={busy}>
                Sign up and ask for a crew
              </Button>
            </div>
          </div>
        </form>
      </div>

      <div className="col gap-16">
        <div className="card">
          <div className="card-body">
            <span className="label">Contact us</span>
            <a href={PHONE_HREF} className="row gap-8 mt-8" style={{ fontSize: 18, fontWeight: 700 }}>
              <Phone size={16} className="amber" />
              {PHONE}
            </a>
            <a href={`mailto:${SUPPORT}`} className="row gap-8 mt-6" style={{ fontSize: 18, fontWeight: 700 }}>
              <Mail size={16} className="amber" />
              {SUPPORT}
            </a>
            <div className="helper">Phone 6am to 8pm daily. Email anytime — we answer the next morning.</div>
          </div>
        </div>
        <SidePanel
          title="What you get"
          items={[
            'A crew at your dock, usually within the hour',
            'Every job has photos and a counted pallet list',
            'One invoice a month, not forty',
            'You can rate the crew, and we use it to pick next time',
          ]}
        />
        <SidePanel
          title="What it costs"
          items={[
            'Per pallet or per hour — quoted before anyone starts',
            'Nights and weekends cost a little more, and you will see it',
            'No retainer, no dispatch fee, no minimum contract',
          ]}
          tone="green"
        />
        <Callout tone="blue" icon={ShieldCheck} title="What happens next">
          You will land in your own dashboard where you can ask for a crew and watch the job. We keep the
          pallet count on both sides so nothing gets argued about later.
        </Callout>
      </div>
    </div>
  );
}
